/**
 * Hard coding constants until we decide on a solution
 * @TODO Remove hard coded constants
 */

const DATESTAMP_MONTH_DATE_HIGHLIGHT = 'month_date_highlight';
const EVENTDETAILS_DISP_TYPE_FILL_LOGOS = 'event_detail_fill_logos';

import {getCatGrpAttributes} from '@stubhub/entity-helper';
import {AWAY_TEAM, HOME_TEAM, getEventPerformerObj} from '@stubhub/react-event-helper';

/**
 * Partnership Theme
 *
 */
const PARTNER_THEME_DYNAMIC_ATTR_NAME = 'name';
const PARTNER_THEME_DYNAMIC_ATTR_VALUE = 'partnerConfig';
const apiThemeAttributeFilter = (element) => {
  return element && element[PARTNER_THEME_DYNAMIC_ATTR_NAME] === PARTNER_THEME_DYNAMIC_ATTR_VALUE;
};

/**
 * Partnership Tagline
 *
 */
const PARTNER_TAGLINE_DYNAMIC_ATTR_VALUE = 'tagline';
const apiTaglineAttributeFilter = (element) => {
  return element && element[PARTNER_THEME_DYNAMIC_ATTR_NAME] === PARTNER_TAGLINE_DYNAMIC_ATTR_VALUE;
};

const NFL_BACKGROUND_COLOR = 'black';

const awayTeamFilter = (element) => {
  return element && element.role === AWAY_TEAM;
};

const homeTeamFilter = (element) => {
  return element && (!element.role || element.role !== AWAY_TEAM);
};

/**
 * Method To Parse the Recommendation API Response to Format partnerConfig.
 * @param groups [array=[]] The Groups Array Object the is returned by the Recommendation API which contains the groups of events that has to be parsed.
 * @returns group [array=[]] The Parsed and Formatted Groups Array object.
 *
 */
export const parseHomeRecoAPIPartnerConfig = (groups = []) => {
  return groups.map((group = {}) => {
    let {partnerConfig} = group;
    if (partnerConfig) {
      try {
        group = {...group, ...getCatGrpAttributes(group)};
        partnerConfig = JSON.parse(partnerConfig);
        partnerConfig = {...partnerConfig, ...constructFullURLForLogos(partnerConfig)};
        group = {...group, partnerConfig, ...getHeroHeaderPartnerOverride(group, partnerConfig)};
      } catch (e) {
        /**
         * TODO - Call Logger Here.
         *
         */
      }
    }

    return group;
  });
};

/**
 * Method to Map Colors to Match the SCSS Variables in _colors global scss variable file.
 * @param performer [object={}] The Performer Object instance from the Catalog Performer API.
 * @returns themeFieldMapping [object] The Field Mapping for Converting the API Variables into the Theme Field Variables.
 *
 */
const getThemeColorMapping = (performer = {}) => {
  /**
   * Default Mapping for Colors to Match the SCSS Variables.
   *
   */
  let themeFieldMapping = {
    secondaryColor: 'paletteSecondary',
  };

  /**
   * NFL Color Mappings Override
   *
   */
  if (performer.isNFL) {
    themeFieldMapping = {
      primaryColor: 'paletteSecondary',
    };
  }

  return themeFieldMapping;
};

/**
 * Method to get the Hero Header Partner Override from the Partner Config.
 * @param entity [object={}] The Performer Object instance from the Catalog Performer API.
 * @param partnerConfig [object={}] The partner Config Dynamic Attribute that is Returned by the API for the current Performer.
 * @returns themeFieldMapping [object] The Field Mapping for Converting the API Variables into the Theme Field Variables.
 *
 */
const getHeroHeaderPartnerOverride = (entity = {}, partnerConfig = {}) => {
  let {secondaryLogoL, primaryColor, primaryHeaderLogo} = partnerConfig;

  /**
   * Default Partner Header Logo, Background Color and Header Partner Logos.
   *
   *
   */
  let heroHeaderTheme = {
    headerLogo: secondaryLogoL,
    headerBackgroundColor: primaryColor,
    headerPartnerLogos: primaryHeaderLogo && [primaryHeaderLogo],
  };

  /**
   * NFL Hero Header Config Override
   *
   */
  if (entity.isNFL) {
    heroHeaderTheme.headerLogo = partnerConfig.primaryLogo;
    heroHeaderTheme.headerBackgroundColor = NFL_BACKGROUND_COLOR;
  }

  return heroHeaderTheme;
};

/**
 * Method to Construct the Full URL for the Logos in the partner Config using the BaseURL and Partner Name present in the dynamic Attribute.
 * @param partnerConfig [object={}] The partner Config Dynamic Attribute that is Returned by the API for the current Performer.
 * @returns processedLogos [object] The Processed Object which has the Image Fields with Full URL.
 *
 */
export const constructFullURLForLogos = (partnerConfig = {}) => {
  let processedLogos = {};
  let {baseURL, name} = partnerConfig;

  if (baseURL && name) {
    let imageFields = [
      'primaryLogo',
      'secondaryLogoL',
      'secondaryLogoR',
      'alternateRectangleLogo',
      'alternateLogo',
      'primaryHeaderLogo',
      'bannerImagery',
    ];

    imageFields.forEach((imageField) => {
      if (Object.prototype.hasOwnProperty.call(partnerConfig, imageField)) {
        processedLogos[imageField] = `/partner/${name}/${partnerConfig[imageField]}.svg`;
      }
    });
  }

  return processedLogos;
};

/**
 * Method to Parse the Performer Object from the API response to get the Partner Config and Format the config to be used in the application.
 * @param performer [object] The Performer Object instance from the Catalog Performer API.
 * @param globalRegistry [object] The Global Registry Object instance.
 * @returns partnerConfig [object] The parsed Partner Config Dynamic Attribute that is Returned by the API for the current Performer.
 *
 */
export const getPartnerEntityTheme = (performer) => {
  let {dynamicAttributes = []} = performer;

  /**
   * Parse the `dynamicAttributes` for Partner Theme
   *
   */
  let partnerSkinOverrides = dynamicAttributes.filter(apiThemeAttributeFilter)[0];

  /**
   * Partner Theme
   *
   */
  let partnerConfig = {};
  let partnerSkinOverridesValue = partnerSkinOverrides && partnerSkinOverrides.value;

  if (partnerSkinOverridesValue) {
    try {
      partnerConfig = JSON.parse(partnerSkinOverridesValue);
    } catch (e) {
      /**
       * TODO - Call Logger Here.
       *
       */
    }

    if (partnerConfig) {
      /**
       * Set a Flag to Denote the Current Entity is a Partner to load the correct layout.
       *
       */
      partnerConfig.isPartner = true;

      /**
       * Pass a variant from the dynamic attributes to determine which event container to display,
       * defaults to partner event container with calendar tab
       *
       */
      partnerConfig.eventListDisplayType = partnerConfig.eventListDisplayType || 'partnerListContainer';

      /**
       * Map Colors to Match the SCSS Variables.
       *
       */
      let themeFieldMapping = getThemeColorMapping(performer);

      Object.keys(themeFieldMapping).forEach((key) => {
        if (partnerConfig[key]) {
          partnerConfig[themeFieldMapping[key]] = partnerConfig[key];
        }
      });

      /**
       * Construct Full URL Path for Images
       *
       */
      partnerConfig = {...partnerConfig, ...constructFullURLForLogos(partnerConfig)};

      /**
       * Header Logo and Background Color.
       *
       */
      partnerConfig = {...partnerConfig, ...getHeroHeaderPartnerOverride(performer, partnerConfig)};

      /**
       * Override the DateStamp Theming to show `month_date_highlight` variant
       *
       */
      partnerConfig.dateTimeStampType = DATESTAMP_MONTH_DATE_HIGHLIGHT;
    }
  }

  /**
   * Parse the `dynamicAttributes` for Partner Tagline && Adding Tagline to the Partnership Configuration
   *
   */
  let partnerTagline = dynamicAttributes.filter(apiTaglineAttributeFilter)[0];
  partnerConfig.partnerText = partnerTagline && partnerTagline.value;

  return partnerConfig;
};

/**
 * Method to Parse the Performer Object from the API response to get the Partner Config and Format the config to be used in the application.
 * @param performerId [string] The ID of the Performer of the current performer page. This is used to determine the `Away and Home Events` and the `Primary and Secondary Performers' for the events
 * @param events [array=[]] The Instance of the Events array from the Search Catalog Events API.
 * @returns processedEvents [array=[]] The parsed Event Array with Parsed Partner Config Dynamic Attribute that is Returned by the API.
 *
 */
export const performerEventsPartnerOverride = (performerId, events = []) => {
  return events.map((event) => {
    /**
     * Set the Event Special Indicator
     *
     */
    let specialIndicator = isAwayEvent(performerId, event);
    if (specialIndicator) {
      event.specialIndicator = specialIndicator;
    } else {
      event.defaultSpecialIndicator = getHomeEvent();
    }

    /**
     * Primary And Secondary Performer Partner Config Object.
     *
     */
    let primaryPerformerObj = getEventPerformerPartnerConfig(performerId, event, true);
    let secondaryPerformerObj = getEventPerformerPartnerConfig(performerId, event, false);

    /**
     * If the Secondary Performer Dose not have the logos, then use the default placeholder
     *
     */
    let primaryPerformerLogos = primaryPerformerObj.getPerformerLogos() || {};
    let {placeholderLogo} = primaryPerformerLogos;

    /**
     * Set the Event Secondary Performer Logo
     *
     */
    let eventCatGrpAttributes = getCatGrpAttributes(event);
    let secondaryPerformerLogo = secondaryPerformerObj.getPerformerLogos() || {};
    if (secondaryPerformerLogo) {
      /**
       * Event Date Card (mWeb)
       *
       */
      event.dateCardPerformerLogo = secondaryPerformerLogo.secondaryLogoL || placeholderLogo;

      /**
       * Event Calendar Card
       *
       */
      event.calendarCardPerformerLogo = secondaryPerformerLogo.primaryLogo || placeholderLogo;
    }

    /**
     * Set the Event Home and Away Performer Logos.
     *
     */
    if (primaryPerformerLogos || secondaryPerformerLogo) {
      event.homePerformerLogo = specialIndicator
        ? primaryPerformerLogos.secondaryLogoL
        : secondaryPerformerLogo.secondaryLogoL || placeholderLogo;
      event.awayPerformerLogo = specialIndicator
        ? secondaryPerformerLogo.secondaryLogoR || placeholderLogo
        : primaryPerformerLogos.secondaryLogoR;
    }

    /**
     * Performer Colors Object
     *
     */
    let primaryPerformerColors = primaryPerformerObj.getPerformerColors() || {};
    let secondaryPerformerColors = secondaryPerformerObj.getPerformerColors() || {};

    /**
     * Set the Event Secondary Performer Color
     *
     */
    if (secondaryPerformerColors) {
      event.dateCardPerformerColor = secondaryPerformerColors.primaryColor;
    }

    /**
     * Set the Event Home and Away Performer Colors.
     * NOTE: Background Color is Disable for the NFL performers
     *
     */
    if (primaryPerformerColors || secondaryPerformerColors) {
      event.homePerformerColor = specialIndicator
        ? primaryPerformerColors.primaryColor
        : secondaryPerformerColors.primaryColor;
      event.awayPerformerColor = specialIndicator
        ? secondaryPerformerColors.primaryColor
        : primaryPerformerColors.primaryColor;
    }

    /**
     * Handle NFL Specific
     *
     */
    if (eventCatGrpAttributes.isNFL) {
      event = nflPartnerThemeOverride(event, secondaryPerformerObj);
    }

    return event;
  });
};

/**
 * Method to Parse the Event Object from the API response to get the Partner Config and Format the config to be used in the application.
 * @param event [object={}] The Instance of the Events Object from the Search Catalog Events API.
 * @returns partnerConfig [object={}] The parsed Parsed Partner Config Dynamic Attribute that is Returned by the API.
 *
 */
export const eventsPartnerOverride = ({...event} = {}) => {
  let partnerConfig = {};
  if (event) {
    /**
     * Primary Performer Partner Config Object.
     *
     */
    let primaryPerformerObj = getAwayPerformerObject(event);
    if (primaryPerformerObj) {
      let primaryPerformerPartnerConfig = parsePerformerPartnerConfig(primaryPerformerObj);
      if (primaryPerformerPartnerConfig) {
        primaryPerformerPartnerConfig = {
          ...primaryPerformerPartnerConfig,
          ...constructFullURLForLogos(primaryPerformerPartnerConfig),
        };
        if (primaryPerformerPartnerConfig.primaryLogo) {
          partnerConfig.primaryPerformerLogo = primaryPerformerPartnerConfig.primaryLogo;
        }
      }
    }

    /**
     * Secondary Performer Partner Config Object.
     *
     */
    let secondaryPerformerObj = getHomePerformerObject(event);
    if (secondaryPerformerObj) {
      let secondaryPerformerPartnerConfig = parsePerformerPartnerConfig(secondaryPerformerObj);
      if (secondaryPerformerPartnerConfig) {
        secondaryPerformerPartnerConfig = {
          ...secondaryPerformerPartnerConfig,
          ...constructFullURLForLogos(secondaryPerformerPartnerConfig),
        };
        if (secondaryPerformerPartnerConfig.primaryLogo) {
          partnerConfig.secondaryPerformerLogo = secondaryPerformerPartnerConfig.primaryLogo;
        }
      }
    }
  }

  return partnerConfig;
};

/**
 * Method to Handle the NFL Specific Partner Configs for Event/Performer Level.
 * @param event [object={}] The Instance of the Event object from the Search Catalog Events API.
 * @param secondaryPerformerObj [object] The Instance of the Performer Object from the Catalog Performer API.
 * @returns event [object={}] The parsed Event Object with Parsed Partner Config Dynamic Attribute that is Returned by the API.
 *
 */
export const nflPartnerThemeOverride = ({...event}, secondaryPerformerObj) => {
  /**
   * Set the Event Secondary Performer Color
   * NOTE: NFL Performers Override the Color to NFL_BACKGROUND_COLOR.
   *
   */
  event.dateCardPerformerColor = NFL_BACKGROUND_COLOR;

  /**
   * NFL Uses `primaryLogo` and MLB uses `secondaryLogoL` for Date card
   *
   */
  if (secondaryPerformerObj && typeof secondaryPerformerObj.getPerformerLogos === 'function') {
    let secondaryPerformerLogo = secondaryPerformerObj.getPerformerLogos() || {};
    event.dateCardPerformerLogo = secondaryPerformerLogo.primaryLogo;
  }

  /**
   * Event Details Panel Display Style Override for NFL
   *
   */
  event.eventDetailsLogoDisplayType = EVENTDETAILS_DISP_TYPE_FILL_LOGOS;

  delete event.homePerformerColor;
  delete event.awayPerformerColor;

  return event;
};

/**
 * Method to Parse the Partner Configs for Performer Level.
 * @param performerObj [object] The Instance of the Performer Object from the Catalog Performer API.
 *
 */
export const parsePerformerPartnerConfig = (performerObj = {}) => {
  let partnerConfig = performerObj.partnerConfig || {};

  try {
    if (partnerConfig) {
      partnerConfig = JSON.parse(partnerConfig);
    }
  } catch (e) {
    /**
     * TODO - Add Logging
     *
     */
  }

  return partnerConfig;
};

/**
 * Method to Handle the NFL Specific Partner Configs for Event/Performer Level.
 * @param primaryPerformerId [string]
 * @param event [object={}] The Instance of the Event object from the Search Catalog Events API.
 * @param primary [boolean] The Instance of the Performer Object from the Catalog Performer API.
 * @returns getPerformerLogos [function] The parsed Event Object with Parsed Partner Config Dynamic Attribute that is Returned by the API.
 *
 */
export const getEventPerformerPartnerConfig = (primaryPerformerId, event, primary) => {
  let performerObj = getEventPerformerObj(primaryPerformerId, event, primary);
  let partnerConfig = parsePerformerPartnerConfig(performerObj);

  return {
    getPerformerLogos: () => {
      let {baseURL, name, primaryLogo, secondaryLogoL, secondaryLogoR, placeholderLogo} = partnerConfig;

      if (baseURL && name) {
        let fullBaseURL = `/partner/${name}`;

        return {
          performerName: performerObj && performerObj.name,
          primaryLogo: primaryLogo ? `${fullBaseURL}/${primaryLogo}.svg` : '',
          secondaryLogoL: secondaryLogoL ? `${fullBaseURL}/${secondaryLogoL}.svg` : '',
          secondaryLogoR: secondaryLogoR ? `${fullBaseURL}/${secondaryLogoR}.svg` : '',
          placeholderLogo: placeholderLogo ? `${fullBaseURL}/${placeholderLogo}.svg` : '',
        };
      }
    },

    getPerformerColors: () => {
      if (partnerConfig) {
        let {primaryColor, secondaryColor} = partnerConfig;

        return {primaryColor, secondaryColor};
      }
    },
  };
};

/**
 * Method To Get the Home Performer Object.
 * @param event [object] The Instance of the Events Object from the Search Catalog Events API.
 *
 */
export const getHomePerformerObject = (event) => {
  if (event) {
    let {performersCollection = [], performers = []} = event;
    if (!performersCollection.length && performers.length) {
      performersCollection = performers;
    }

    return performersCollection.filter(homeTeamFilter)[0];
  }
};

/**
 * Method To Get the Away Performer Object.
 * @param event [object] The Instance of the Events Object from the Search Catalog Events API.
 *
 */
export const getAwayPerformerObject = (event) => {
  if (event) {
    let {performersCollection = [], performers = []} = event;
    if (!performersCollection.length && performers.length) {
      performersCollection = performers;
    }

    return performersCollection.filter(awayTeamFilter)[0];
  }
};

/**
 * Method To Check if the Event is Away game for the given performer.
 * @param performerId [number] The performer id of page where the event is to be displayed.
 * @param event [object] The Instance of the Events Object from the Search Catalog Events API.
 *
 */
export const isAwayEvent = (performerId, event) => {
  if (event && performerId) {
    let awayTeamObj = getAwayPerformerObject(event);

    /**
     * If Away Team Object has same PerformerId as the Current performer.
     *
     */
    if (awayTeamObj && parseInt(awayTeamObj.id) === parseInt(performerId)) {
      return AWAY_TEAM;
    }
  }
};

/**
 * Method To get the Home Team Placeholder Text.
 *
 */
export const getHomeEvent = () => {
  return HOME_TEAM;
};

/**
 * Method To get PartnerConfig
 *
 */
export const getPartnerConfig = (performer = {}) => {
  let {dynamicAttributes = []} = performer;

  return dynamicAttributes.filter((dynamicAttribute = {}) => dynamicAttribute.name === 'partnerConfig');
};

/**
 * Method To get PartnerConfigValueObj
 *
 */
export const getPartnerConfigValueObj = (dynamicAttributes = []) => {
  let getPartnerConfigValueObj = dynamicAttributes.map((dynamicAttribute = {}) => dynamicAttribute.value);

  return JSON.parse(getPartnerConfigValueObj);
};
